// ── SettingsAgentConfigPage ─────────────────────────────────────────────────
//
// CFGIA.A3 (#1538) — Settings → Agents. Tenant-level agent configuration
// editor that surfaces the wizard's Step4AgentConfig on a standalone settings
// surface so admins can edit it after onboarding. Reuses the existing wizard
// component (no parallel copy); writes the same store (tenant_agent_configs via
// GET/POST /tenants/:tenantId/agent-configs, admin-write).
//
// D11 — the per-agent AI Model selector is removed entirely (llm_model /
// llm_provider columns dropped). Model selection stays GOVERNED in
// /settings/agent-models (its own propose/approve flow + agent_model_config
// store, which is what the runtime reads via getActiveModel). This section
// only surfaces the config with no other home: phase activation,
// system-prompt overrides, and checklist defaults.

import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Step4AgentConfig from '../components/wizard/steps/Step4AgentConfig';
import { HelpPopover } from '../components/help';
import { useMyTenant, isAdminRole } from '../hooks/useMyTenant';

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-md)',
  padding: 20,
  marginBottom: 16,
};

export default function SettingsAgentConfigPage() {
  const { myTenant, loading } = useMyTenant();
  const tenantId = myTenant?.tenantId;
  const isAdmin = isAdminRole(myTenant?.role);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      <Sidebar />
      <main style={{ flex: 1, padding: 32, maxWidth: 760 }}>
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/settings"
            style={{
              fontFamily: 'var(--font-body)',
              fontSize: 13,
              color: 'var(--text-secondary)',
              textDecoration: 'none',
            }}
          >
            ← Back to settings
          </Link>
        </div>

        {/* Page owns the <h1>; the mounted Step4AgentConfig suppresses its own
            <h3> title via showIntro={false} so the sub-page has a single, clean
            heading outline (one <h1>, no skipped level). CFGIA.A3 double-title
            resolution — see decisions log: the heading-vs-frozen-core fork
            resolved to the showIntro prop (the no-touch CSS alternative was
            untestable in the test harness). Mirrors QAPolicySettingsPage. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <h1
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 24,
              fontWeight: 700,
              color: 'var(--text)',
              margin: 0,
            }}
          >
            Agents
          </h1>
          <HelpPopover
            id="agents-settings"
            variant="info"
            heading="Agent configuration"
            body={
              <>
                <p style={{ margin: '0 0 8px' }}>
                  Choose which agents are active for each phase and customise their system-prompt
                  prefixes and organisation-minimum exit-checklist items. Changes apply tenant-wide
                  and take effect on the next session.
                </p>
                <p style={{ margin: 0 }}>
                  Per-agent model selection lives under{' '}
                  <Link to="/settings/agent-models" style={{ color: 'var(--primary)' }}>
                    Agent Model Configuration
                  </Link>
                  , which carries its own change-approval flow. Only tenant admins can edit.
                </p>
              </>
            }
          />
        </div>
        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 14,
            color: 'var(--text-secondary)',
            marginBottom: 24,
          }}
        >
          Configure which agents run for each phase, their system-prompt prefixes, and the
          organisation-minimum exit-checklist items applied across your projects.
        </p>

        {loading || !tenantId ? (
          <div style={{ ...cardStyle, fontFamily: 'var(--font-body)', color: 'var(--text-secondary)' }}>
            Loading…
          </div>
        ) : (
          <div style={cardStyle}>
            <Step4AgentConfig
              tenantId={tenantId}
              canEdit={isAdmin}
              showIntro={false}
              submitLabel="Save changes"
            />
          </div>
        )}
      </main>
    </div>
  );
}
